import type { KeyboardEvent, RefObject } from "react";
import { useLayoutEffect } from "react";

// The recording instruction field: a textarea that wraps long instructions
// (clinical lead, 01/11/2026: a single input kept them on one line) and
// grows with them up to a few lines, then scrolls. An instruction is still
// one line of text: Enter adds it, as in the field it replaces, and line
// breaks (pasted or dictated) become spaces.

export const INSTRUCTION_FIELD_MAX_HEIGHT = 128;

// One line: the field it replaces kept no line breaks.
export const singleLine = (value: string): string =>
  value.replace(/\r\n?|\n/g, " ");

// Grows with the text, up to `max` px, then scrolls.
export const useAutoGrow = (
  field: RefObject<HTMLTextAreaElement | null>,
  value: string,
  max: number = INSTRUCTION_FIELD_MAX_HEIGHT,
): void => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured per value.
  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    const height = Math.min(element.scrollHeight, max);
    element.style.height = `${height}px`;
    element.style.overflowY = element.scrollHeight > max ? "auto" : "hidden";
  }, [value, max]);
};

// Enter adds the instruction (Shift+Enter too: no line breaks), except
// while an input method is composing a word (Japanese, Chinese).
export const submitOnEnter = (
  event: KeyboardEvent<HTMLTextAreaElement>,
  submit: () => void,
): void => {
  if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
  event.preventDefault();
  submit();
};
